/**
 * The settings-specific pieces: a page frame, the active/inactive badge, the
 * `is_system` lock, and the show-inactive toggle. They live here rather than in
 * the design system because they encode *MeshHub's* configuration rules —
 * "deactivate, never delete", "system rows are locked" — not generic visuals.
 *
 * The form primitives moved to `components/FormKit.web.jsx` in Step 3, when
 * contact editing started needing the same dialogs and fields. They are
 * re-exported here so the six settings screens import from one place, and so
 * there is exactly one definition of each.
 */
import { Badge } from '@meshhub/design-tokens/ui';
import { Lock } from 'lucide-react';
import React from 'react';
import { S } from '../../lib/strings.js';

export {
  CheckboxField,
  ConfirmDialog,
  FormDialog,
  LoadingOrEmpty,
  MutationError,
  SelectField,
  TextField,
} from '../FormKit.web.jsx';

export function SettingsPage({ title, hint, toolbar, children }) {
  return (
    <div className="flex h-screen flex-col">
      <header className="flex h-toolbar shrink-0 items-center gap-4 border-b border-border bg-surface px-6">
        <h1 className="text-lg font-bold">{title}</h1>
        {hint && <span className="text-sm text-text-muted">{hint}</span>}
        <div className="ms-auto flex items-center gap-3">{toolbar}</div>
      </header>
      <div className="min-h-0 flex-1 overflow-auto p-6">{children}</div>
    </div>
  );
}

export function ActiveBadge({ isActive }) {
  return (
    <Badge className={isActive ? 'bg-surface-hover text-success' : 'bg-surface-hover text-text-muted'}>
      {isActive ? S.app.active : S.app.inactive}
    </Badge>
  );
}

/** `is_system` rows: labels editable, the row itself untouchable. */
export function SystemLock({ isSystem }) {
  if (!isSystem) return null;
  return (
    <span className="flex items-center gap-1 text-xs text-text-muted" title={S.settings.common.systemLocked}>
      <Lock size={12} aria-hidden="true" />
      {S.app.system}
    </span>
  );
}

export function ShowInactiveToggle({ checked, onChange }) {
  return (
    <label className="flex items-center gap-2 text-sm text-text-muted">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="accent-primary"
      />
      {S.settings.common.showInactive}
    </label>
  );
}
